import { apiClient } from '@/core/api/apiClient'
import { getDriverLicenseUploadContentType } from '@/components/DriversLicensePreview'
import { fileToBase64 } from '@/core/utils/fileUtils'
import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useState } from 'react'

const extractUploadResult = (result: unknown): { storagePath?: string; url?: string } => {
  const data = (result as { data?: { storagePath?: string; url?: string } })?.data ?? result
  return data as { storagePath?: string; url?: string }
}

const extractUrl = (result: unknown): string | undefined => {
  const data = (result as { data?: { url?: string } })?.data ?? result
  return (data as { url?: string })?.url
}

export const useClientDriversLicenseAdmin = (
  clientUserId: string | undefined,
  storagePath: string | undefined,
) => {
  const queryClient = useQueryClient()
  const path = storagePath ?? ''
  const hasLicense = !!path

  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [isLoadingUrl, setIsLoadingUrl] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const invalidateUsers = () => {
    void queryClient.invalidateQueries({ queryKey: ['users'] })
  }

  const loadPreviewUrl = useCallback(async () => {
    if (!clientUserId || !path) {
      setPreviewUrl(null)
      return
    }

    setIsLoadingUrl(true)
    setError(null)
    try {
      const result = await apiClient.portal.admin.getClientDriverLicenseFrontUrl(path, clientUserId)
      const url = extractUrl(result)
      setPreviewUrl(url ?? null)
    } catch (err) {
      console.error('Failed loading client driver license preview:', err)
      setError(err instanceof Error ? err.message : 'Failed to load license photo')
      setPreviewUrl(null)
    } finally {
      setIsLoadingUrl(false)
    }
  }, [clientUserId, path])

  useEffect(() => {
    void loadPreviewUrl()
  }, [loadPreviewUrl])

  const uploadLicense = async (file: File) => {
    if (!clientUserId) {
      throw new Error('No client selected')
    }

    setIsUploading(true)
    setError(null)
    try {
      const base64 = await fileToBase64(file)
      const result = await apiClient.portal.admin.uploadClientDriverLicenseFront(
        base64,
        clientUserId,
        getDriverLicenseUploadContentType(file),
      )
      const { url } = extractUploadResult(result)
      if (url) {
        setPreviewUrl(url)
      } else {
        await loadPreviewUrl()
      }
      invalidateUsers()
    } catch (err) {
      console.error('Failed uploading client driver license:', err)
      setError(err instanceof Error ? err.message : 'Failed to upload license photo')
      throw err
    } finally {
      setIsUploading(false)
    }
  }

  const removeLicense = async () => {
    if (!clientUserId) {
      throw new Error('No client selected')
    }

    setIsUploading(true)
    setError(null)
    try {
      await apiClient.portal.admin.removeClientDriverLicenseFront(clientUserId)
      setPreviewUrl(null)
      invalidateUsers()
    } catch (err) {
      console.error('Failed removing client driver license:', err)
      setError(err instanceof Error ? err.message : 'Failed to remove license photo')
      throw err
    } finally {
      setIsUploading(false)
    }
  }

  return {
    hasLicense,
    previewUrl,
    isUploading,
    isLoadingUrl,
    error,
    uploadLicense,
    removeLicense,
    loadPreviewUrl,
  }
}
